/**
 * QuickBooks Integration Service
 *
 * Handles API calls for QuickBooks OAuth, invoices, and payment management.
 */

import { api } from './apiClient';

// ===== TYPES =====

export interface QuickBooksStatus {
  connected: boolean;
  company_name?: string;
  company_id?: string;
  expires_at?: string;
  refresh_token_expires_at?: string;
  last_synced_at?: string;
  needs_reconnect?: boolean;
  is_expired?: boolean;
  connected_by?: string;
  connected_at?: string;
  company_info_error?: string;
}

export interface ShippingBreakdown {
  total_lbs?: number;
  total_bags?: number;
  pallets?: number;
  is_additional_delivery?: boolean;
  customer_type?: string;
  rule?: string;
  bags_per_pallet?: number;
  free_shipping?: boolean;
  base_shipping?: number;
  liftgate?: number;
  liftgate_required?: boolean;
  residential_farm?: number;
  residential_delivery?: boolean;
  method?: string;
  details?: string;
  error?: string;
}

export interface QBInvoice {
  id: number;
  order: number;  // Backend field is 'order', not 'sales_order'
  order_number: string;
  customer_name: string;
  qb_invoice_id: string | null;
  qb_invoice_number: string | null;
  invoice_number?: string | null;  // Native invoice number
  status: 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'SENT' | 'PAID' | 'PARTIALLY_PAID' | 'OVERDUE' | 'VOIDED' | 'draft' | 'pending_review' | 'approved' | 'sent' | 'paid' | 'partial' | 'void';
  subtotal: string;
  shipping_cost: string;
  shipping_breakdown: ShippingBreakdown | null;
  shipping_override: string | null;
  shipping_override_reason: string;
  shipping_quote_number?: string | null;
  tax_amount?: string;  // Backend may return 'tax' instead
  tax?: string;  // Backend field name
  total_amount?: string;  // Backend may return 'total' instead
  total?: string;  // Backend field name
  amount_paid?: string;  // Backend may return 'paid_amount'
  paid_amount?: string;  // Backend field name
  balance_due: string;
  invoice_date?: string;  // Optional - backend uses created_at
  due_date: string;
  sent_at: string | null;
  paid_at: string | null;
  payment_link: string | null;
  needs_qb_sync?: boolean;
  created_at: string;
  updated_at?: string;
  last_synced_at?: string | null;
  notes: string;
  is_overdue?: boolean;
}

export interface InvoiceReviewTask {
  id: number;
  invoice: number;
  invoice_number: string;
  customer_name: string;
  order_number: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED';
  assigned_to: number | null;
  assigned_to_name: string | null;
  notes: string;
  shipping_cost_added: string;
  created_at: string;
  completed_at: string | null;
}

export interface InvoiceReviewData {
  // shipping_cost was removed on purpose: the Review & Approve shipping box
  // silently zeroed real shipping when left blank (the Osprey Hill trap).
  // Approving never touches shipping; edit shipping via updateInvoice.
  notes?: string;
}

export interface PortalInvoice {
  id: number;
  invoice_number: string;
  order_number: string;
  status: string;
  total: string;  // Backend returns 'total'
  total_amount?: string;  // Legacy field alias
  paid_amount: string;
  credits_applied: string;
  customer_credits_applied?: string;
  balance_due: string;
  invoice_date?: string;  // Optional - backend may return 'date' or 'created_at' instead
  date?: string;  // Alternative field name from backend
  created_at?: string;  // Fallback field
  due_date: string;
  payment_link: string | null;
  is_overdue: boolean;
  ach_initiated_at?: string | null;  // When ACH payment was started (ISO datetime)
}

export interface OrderNeedingInvoice {
  id: number;
  order_number: string;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  customer_address: string;
  customer_city: string;
  customer_state: string;
  customer_zip: string;
  customer_tax_exempt: boolean;
  order_date: string;
  delivery_date: string | null;
  ship_by_date?: string | null;
  shipped_date?: string | null;
  payment_terms?: string;
  payment_terms_days?: number;
  calculated_due_date?: string | null;
  total: string;
  status: string;
  shipping_method: string;
  notes: string;
  liftgate_required?: boolean;
  customer_type?: string;
  estimated_shipping?: number;
  shipping_breakdown?: {
    pallets: number;
    base_cost: number;
    farm_fee: number;
    liftgate_fee: number;
    retail_discount: number;
    total: number;
    total_weight_lbs?: number;
    breakdown_text: string;
  };
  items: {
    id: number;
    feed_name: string;
    quantity_lbs: number;
    unit_price: string;
    line_total: string;
    package_type: string;
    is_pelleted: boolean;
    // Bag pricing fields
    number_of_packages: number;
    price_per_package: string | null;
    pricing_display_method: 'PER_LB' | 'PER_PACKAGE';
  }[];
}

export interface CreateInvoiceData {
  shipping_cost: number;
  additional_fee?: number;
  additional_fee_description?: string;
  notes?: string;
  due_date?: string;
  send_immediately?: boolean;
}

export interface CreatedInvoice {
  id: number;
  invoice_number: string;
  status: string;
  subtotal: string;
  shipping_cost: string;
  tax: string;
  total: string;
  due_date: string;
  sent: boolean;
  qb_synced: boolean;
}

export interface QuickBooksItem {
  id: number;
  qb_id: string;
  name: string;
  full_name: string;
  item_type: 'Service' | 'Inventory' | 'NonInventory' | 'Group' | 'Bundle';
  description: string;
  income_account_name: string;
  unit_price: string | null;
  active: boolean;
  synced_at: string;
}

export interface QuickBooksSettings {
  id: number;
  default_feed_item: number | null;
  default_feed_item_name: string | null;
  shipping_item: number | null;
  shipping_item_name: string | null;
  additional_fee_item: number | null;
  additional_fee_item_name: string | null;
  last_items_sync: string | null;
  auto_sync_items: boolean;
}

export interface FormulaItemMapping {
  id: number;
  name: string;
  qb_item_id: string | null;
  qb_item_name: string | null;
}

// ===== ADMIN: QUICKBOOKS CONNECTION =====

export const getQuickBooksStatus = async (): Promise<QuickBooksStatus> => {
  return api.get<QuickBooksStatus>('/api/quickbooks/status/');
};

export const connectQuickBooks = async (): Promise<{ authorization_url: string }> => {
  return api.get<{ authorization_url: string }>('/api/quickbooks/connect/');
};

export const disconnectQuickBooks = async (): Promise<{ message: string }> => {
  return api.post<{ message: string }>('/api/quickbooks/disconnect/');
};

// ===== ADMIN: INVOICE MANAGEMENT =====

export const getOrdersNeedingInvoice = async (): Promise<{
  orders: OrderNeedingInvoice[];
  count: number;
}> => {
  return api.get<{ orders: OrderNeedingInvoice[]; count: number }>('/api/orders/needs-invoice/');
};

export const bulkArchiveNoInvoice = async (orderIds: number[]): Promise<{
  success: boolean;
  archived_count: number;
  message: string;
}> => {
  return api.post<{ success: boolean; archived_count: number; message: string }>(
    '/api/orders/bulk-archive-no-invoice/',
    { order_ids: orderIds }
  );
};

export const bulkArchiveAllNoInvoice = async (): Promise<{
  success: boolean;
  archived_count: number;
  message: string;
}> => {
  return api.post<{ success: boolean; archived_count: number; message: string }>(
    '/api/orders/bulk-archive-all-no-invoice/'
  );
};

export const createInvoice = async (
  orderId: number,
  data: CreateInvoiceData
): Promise<{ success: boolean; invoice: CreatedInvoice }> => {
  return api.post<{ success: boolean; invoice: CreatedInvoice }>(
    `/api/orders/${orderId}/create-invoice/`,
    data
  );
};

export const getInvoices = async (params?: {
  status?: string;
  customer?: number;
  search?: string;
}): Promise<QBInvoice[]> => {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.customer) queryParams.append('customer', params.customer.toString());
  if (params?.search) queryParams.append('search', params.search);

  const query = queryParams.toString();
  return api.get<QBInvoice[]>(`/api/invoices/${query ? `?${query}` : ''}`);
};

export const getInvoice = async (id: number): Promise<QBInvoice> => {
  return api.get<QBInvoice>(`/api/invoices/${id}/`);
};

export const reviewInvoice = async (id: number, data: InvoiceReviewData): Promise<QBInvoice> => {
  return api.post<QBInvoice>(`/api/invoices/${id}/review/`, data);
};

export const sendInvoice = async (id: number, recipients?: string[], includeTracking?: boolean): Promise<any> => {
  return api.post(`/api/invoices/${id}/send/`, {
    ...(recipients ? { recipients } : {}),
    ...(includeTracking ? { include_tracking: true } : {}),
  });
};

export const getInvoiceRecipients = async (id: number) => {
  return api.get(`/api/invoices/${id}/recipients/`);
};

export const getInvoiceEmails = async (id: number) => {
  return api.get(`/api/invoices/${id}/emails/`);
};

export const updateInvoice = async (
  id: number,
  data: {
    payment_link?: string;
    notes?: string;
    customer_notes?: string;
    shipping_override?: string | null;
    shipping_override_reason?: string;
    shipping_quote_number?: string;
    shipping_cost?: string;
    tax?: string;
    due_date?: string;
  }
): Promise<any> => {
  return api.patch(`/api/invoices/${id}/`, data);
};

export const addInvoiceItem = async (
  invoiceId: number,
  data: {
    formula_id?: number;
    product_id?: number;
    package_type?: string;
    total_quantity_lbs?: number;
    number_of_packages?: number;
    quantity?: number;
    unit_price_per_lb?: number;
    price_per_package?: number;
    price_per_unit?: number;
    is_pelleted?: boolean;
    is_promo?: boolean;
    promo_reason?: string;
    campaign_enrollment?: number | null;
    notes?: string;
  }
): Promise<any> => {
  return api.post(`/api/invoices/${invoiceId}/items/`, data);
};

export const editInvoiceItem = async (
  invoiceId: number,
  itemId: number,
  data: {
    unit_price_per_lb?: string;
    price_per_package?: string;
    line_total?: string;
    total_quantity_lbs?: string;
    number_of_packages?: number;
    notes?: string;
  }
): Promise<any> => {
  return api.patch(`/api/invoices/${invoiceId}/items/${itemId}/`, data);
};

export const deleteInvoiceItem = async (
  invoiceId: number,
  itemId: number
): Promise<any> => {
  return api.delete(`/api/invoices/${invoiceId}/items/${itemId}/`);
};

export const syncInvoice = async (id: number, force: boolean = false): Promise<QBInvoice> => {
  return api.post<QBInvoice>(`/api/invoices/${id}/sync/`, { force });
};

export const createInvoiceFromOrder = async (orderId: number): Promise<QBInvoice> => {
  return api.post<QBInvoice>(`/api/orders/${orderId}/create-invoice/`);
};

export const getReviewTasks = async (status?: string): Promise<InvoiceReviewTask[]> => {
  const query = status ? `?status=${status}` : '';
  return api.get<InvoiceReviewTask[]>(`/api/invoices/review-tasks/${query}`);
};

// ===== CUSTOMER PORTAL: INVOICES =====

export const getPortalInvoices = async (): Promise<PortalInvoice[]> => {
  return api.get<PortalInvoice[]>('/api/portal/invoices/');
};

export const getPortalInvoice = async (id: number): Promise<PortalInvoice> => {
  return api.get<PortalInvoice>(`/api/portal/invoices/${id}/`);
};

export const getPaymentLink = async (id: number): Promise<{ payment_url: string }> => {
  return api.post<{ payment_url: string }>(`/api/portal/invoices/${id}/pay/`);
};

// ===== STATUS HELPERS =====

export const getStatusColor = (status: string): string => {
  // Normalize to lowercase for comparison (backend returns lowercase)
  const s = status?.toLowerCase() || '';
  switch (s) {
    case 'draft':
      return '#6b7280';
    case 'pending_review':
      return '#f59e0b';
    case 'approved':
      return '#3b82f6';
    case 'sent':
      return '#8b5cf6';
    case 'ach_pending':
      return '#d97706';
    case 'paid':
      return '#22c55e';
    case 'partial':
    case 'partially_paid':
      return '#06b6d4';
    case 'overdue':
      return '#ef4444';
    case 'void':
    case 'voided':
      return '#9ca3af';
    default:
      return '#6b7280';
  }
};

export const getStatusLabel = (status: string): string => {
  // Normalize to lowercase for comparison (backend returns lowercase)
  const s = status?.toLowerCase() || '';
  switch (s) {
    case 'draft':
      return 'Draft';
    case 'pending_review':
      return 'Pending Review';
    case 'approved':
      return 'Approved';
    case 'sent':
      return 'Sent';
    case 'ach_pending':
      return 'ACH Processing';
    case 'paid':
      return 'Paid';
    case 'partial':
    case 'partially_paid':
      return 'Partially Paid';
    case 'overdue':
      return 'Overdue';
    case 'void':
    case 'voided':
      return 'Voided';
    default:
      return status;
  }
};

// ===== ADMIN: QUICKBOOKS ITEMS =====

export const getQuickBooksItems = async (params?: {
  item_type?: string;
  search?: string;
  active_only?: boolean;
}): Promise<QuickBooksItem[]> => {
  const queryParams = new URLSearchParams();
  if (params?.item_type) queryParams.append('item_type', params.item_type);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.active_only) queryParams.append('active_only', 'true');

  const query = queryParams.toString();
  const response = await api.get<{ items: QuickBooksItem[]; count: number }>(
    `/api/quickbooks/items/${query ? `?${query}` : ''}`
  );
  return response.items || [];
};

export const syncQuickBooksItems = async (): Promise<{
  success: boolean;
  synced_count: number;
  message: string;
  products_created?: number;
  products_updated?: number;
}> => {
  return api.post<{ success: boolean; synced_count: number; message: string; products_created?: number; products_updated?: number }>(
    '/api/quickbooks/sync-items/'
  );
};

export const getQBSettings = async (): Promise<QuickBooksSettings> => {
  return api.get<QuickBooksSettings>('/api/quickbooks/settings/');
};

export const updateQBSettings = async (data: Partial<QuickBooksSettings>): Promise<QuickBooksSettings> => {
  return api.post<QuickBooksSettings>('/api/quickbooks/settings/', data);
};

export const getFormulaItemMappings = async (): Promise<FormulaItemMapping[]> => {
  const response = await api.get<FormulaItemMapping[] | { mappings: FormulaItemMapping[]; results: FormulaItemMapping[] }>(
    '/api/quickbooks/item-mappings/'
  );
  // Handle both array and object responses
  if (Array.isArray(response)) {
    return response;
  }
  return response.mappings || response.results || [];
};

export const updateFormulaItemMapping = async (
  formulaId: number,
  data: { qb_item_id: string | null; qb_item_name: string | null }
): Promise<{ success: boolean }> => {
  return api.post<{ success: boolean }>(
    `/api/quickbooks/item-mappings/${formulaId}/`,
    data
  );
};

export const bulkUpdateItemMappings = async (
  mappings: { formula_id: number; qb_item_id: string | null; qb_item_name: string | null }[]
): Promise<{ success: boolean; updated_count: number }> => {
  return api.post<{ success: boolean; updated_count: number }>(
    '/api/quickbooks/item-mappings/bulk/',
    { mappings }
  );
};

// ===== QB CUSTOMER MAPPING =====

export interface QBCustomer {
  id: string;
  display_name: string;
  company_name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  active: boolean;
}

export interface AppCustomerMapping {
  id: number;
  name: string;
  email: string;
  city: string;
  state: string;
  qb_customer_id: string | null;
  match_confidence?: string;
}

export interface QBCustomersResponse {
  qb_customers: QBCustomer[];
  app_customers: AppCustomerMapping[];
}

export interface QBAutoMatchMatch {
  customer_id: number;
  customer_name: string;
  qb_id: string;
  qb_name: string;
  confidence: string;
}

export interface QBAutoMatchResult {
  success: boolean;
  already_linked: number;
  newly_matched: number;
  matches: QBAutoMatchMatch[];
  unmatched_count: number;
  unmatched: AppCustomerMapping[];
}

export const getQBCustomers = async (): Promise<QBCustomersResponse> => {
  return api.get<QBCustomersResponse>('/api/quickbooks/customers/');
};

export const linkQBCustomer = async (
  customerId: number,
  qbCustomerId: string | null
): Promise<{ success: boolean }> => {
  return api.post('/api/quickbooks/customers/link/', {
    customer_id: customerId,
    qb_customer_id: qbCustomerId,
  });
};

export const autoMatchQBCustomers = async (): Promise<QBAutoMatchResult> => {
  return api.post<QBAutoMatchResult>('/api/quickbooks/customers/auto-match/');
};

export const unlinkAllQBCustomers = async (): Promise<{ success: boolean; unlinked_count: number }> => {
  return api.post('/api/quickbooks/customers/unlink-all/');
};

export interface AIMatchProposal {
  app_id: number;
  app_name: string;
  qb_id: string;
  qb_name: string;
  confidence: string;
  reason: string;
}

export interface AIMatchResult {
  success: boolean;
  proposals: AIMatchProposal[];
  exact_matched: number;
  ai_proposed: number;
  unmatched_count: number;
}

export const aiMatchQBCustomers = async (): Promise<AIMatchResult> => {
  return api.post<AIMatchResult>('/api/quickbooks/customers/ai-match/');
};

// ===== QB TIER MAPPINGS =====

export interface QBTierMapping {
  id: number;
  formula_id: number;
  formula_name: string;
  formula_code: string;
  formula_feed_grade: string;
  formula_animal_type: string;
  price_tier: string;
  price_tier_display: string;
  qb_item_id: string;
  qb_item_name: string;
  match_method: string;
}

export interface TierFormula {
  id: number;
  name: string;
  code: string;
  feed_grade: string;
  animal_type: string;
  has_tiers: Record<string, boolean>;
}

export interface TierMappingsResponse {
  mappings: QBTierMapping[];
  formulas: TierFormula[];
}

export interface AutoMatchProposal {
  qb_item_id: string;
  qb_item_name: string;
  qb_category: string;
  formula_id: number;
  formula_name: string;
  price_tier: string;
  form: string;
  confidence: string;
  parsed_core: string;
  source?: string;
}

export interface AutoMatchResult {
  proposed: AutoMatchProposal[];
  proposed_count: number;
  unmatched_qb: { qb_item_id: string; qb_item_name: string; qb_category: string; parsed_core: string; parsed_tier: string }[];
  unmatched_qb_count: number;
  unmatched_formulas: { id: number; name: string; feed_grade: string }[];
  unmatched_formulas_count: number;
}

export const getTierMappings = async (): Promise<TierMappingsResponse> => {
  return api.get<TierMappingsResponse>('/api/quickbooks/tier-mappings/');
};

export const saveTierMapping = async (
  formulaId: number, priceTier: string, qbItemId: string | null, qbItemName?: string
): Promise<{ success: boolean }> => {
  return api.post('/api/quickbooks/tier-mappings/save/', {
    formula_id: formulaId, price_tier: priceTier,
    qb_item_id: qbItemId, qb_item_name: qbItemName || '',
  });
};

export const bulkSaveTierMappings = async (
  mappings: { formula_id: number; price_tier: string; qb_item_id: string; qb_item_name: string; match_method?: string }[]
): Promise<{ success: boolean; saved: number }> => {
  return api.post('/api/quickbooks/tier-mappings/bulk/', { mappings });
};

export const deleteTierMapping = async (mappingId: number): Promise<{ success: boolean }> => {
  return api.delete(`/api/quickbooks/tier-mappings/${mappingId}/`);
};

export const clearAllTierMappings = async (): Promise<{ success: boolean; deleted: number }> => {
  return api.post('/api/quickbooks/tier-mappings/clear-all/');
};

export const autoMatchItems = async (): Promise<AutoMatchResult> => {
  return api.post<AutoMatchResult>('/api/quickbooks/items/auto-match/');
};

export interface AIItemMatchResult {
  proposed: (AutoMatchProposal & { source: string })[];
  proposed_count: number;
  regex_count: number;
  ai_count: number;
  unmatched_qb_count: number;
  unmatched_formulas: { id: number; name: string; feed_grade: string }[];
  unmatched_formulas_count: number;
}

export const aiMatchItems = async (): Promise<AIItemMatchResult> => {
  return api.post<AIItemMatchResult>('/api/quickbooks/items/ai-match/');
};

export const applySuggestions = async (
  suggestions: { formula_id: number; price_tier: string; qb_item_id: string; qb_item_name: string }[]
): Promise<{ success: boolean; saved: number }> => {
  return api.post('/api/quickbooks/items/apply-suggestions/', { suggestions });
};

// ===== QB SYNC STATUS & RETRY =====

export interface QBSyncStatus {
  pending_invoices: number;
  pending_customers: number;
  total_pending: number;
}

export const getQBSyncStatus = async (): Promise<QBSyncStatus> => {
  return api.get<QBSyncStatus>('/api/quickbooks/sync-status/');
};

export const retryQBSync = async (): Promise<{
  success: boolean;
  invoices_synced: number;
  invoices_failed: number;
  customers_synced: number;
  customers_failed: number;
}> => {
  return api.post('/api/quickbooks/retry-sync/');
};
